import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { WalletService } from './wallet.service';
import { AuthService } from './auth.service';
import { LanguageService } from './language.service';
import { Observable, of, throwError, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  HotelOffer, HotelRoom, RatePlan, HotelSearchCriteria, HotelSearchResult, HotelNameSuggestion, Board, BookingStatus, Reservation, SelectedRate,
  Source, SearchSource, currencyFractionDigits, exactMoney, lineTotalsOf, nights, resGrandTotal, TODAY, titleCaseCode,
} from '../models';

export interface BookingRequest {
  hotel: HotelOffer;
  crit: HotelSearchCriteria;
  lines: SelectedRate[];   // multi-room cart; one entry per room type + rate plan
  lead: { title: string; first: string; last: string; email: string; phone: string };
  billing: { street: string; city: string; zip: string; country: string };
  currency: string;
  idempotencyKey: string;
}
export interface CancellationResult { status: 'CANCELLED'; refundAmount: number; fee: number; }

/** Thrown by FlightSearchHotelSourcing.getOffer() when the Amadeus connector reports the
 *  cached offer is gone (410) or the hotel just sold out (409) — see HotelConnectorClientExceptionMapper
 *  in be-flight-search-service. Extranet has no equivalent (its pricing is recomputed live, never cached),
 *  so components must opt in to catching these rather than the shared interface always throwing. */
export class OfferExpiredError extends Error {}
export class OfferUnavailableError extends Error {}

// Page = ONE priced chunk — 24 (was 12 from WI #3196, 50 since WI #1063/#1086, 100 before that).
// HotelSearchServiceV2 prices candidates in fixed chunks of `hotel.search.v2.chunk-size` (18,
// independent of pageSize — the old "chunk size IS pageSize" note here was stale) and walks chunk
// after chunk, SEQUENTIALLY, one Amadeus round trip each (~5s), until pageSize hotels survive.
// Asking for 50 up front meant ~6 chunks (~70s measured in prod, 2026-09-03) before the first card
// could render; 12 was "the survivors of one chunk" while ~40% of a chunk came back with rooms.
// Since the BE's weekly availability score + exclusion (WI #3363) a chunk yields ~25 hotels with
// offers, so 12 meant two of every three pages were pure round trips for hotels the BE already
// had in its chunk cache (prod 2026-09-18: 1.5-1.9s each), one in three waited for the next chunk.
// 24 ≈ the survivors of one chunk again (WI #3369): first paint still after a single round trip,
// then ~24 hotels per round instead of 10-12 with pauses. The rest streams in through
// loadMore()/auto-continue (search.component.ts) while the BE prefetches the next chunks off the
// request path so those pages hit a warm chunk cache. The BE's own default for a missing pageSize
// is the same 24. Exported (not just a private static on the class) because search.component.ts's
// auto-continue needs the same numbers, and it can only reach FlightSearchHotelSourcing through
// the HOTEL_SOURCING token's abstract HotelSourcingService type, which carries no such constant.
export const HOTEL_SEARCH_PAGE_SIZE = 24;
/** How many hotels the auto-continue keeps fetching towards before handing over to the manual
 *  "Load more" button — the previous single-page size, so the agent still ends up with the same
 *  first batch as before, just progressively instead of after one long wait. */
export const HOTEL_SEARCH_INITIAL_TARGET = 50;
/** Progressive load (WI #3206): after the first page the list keeps loading page after page on its
 *  own until the BE says there is nothing left — the agent sees the whole destination fill in,
 *  nearest hotels first (the BE walks candidates by distance). This is only a runaway guard: a
 *  destination with more priceable hotels than this hands over to the manual "Load more". */
export const HOTEL_SEARCH_AUTO_LOAD_CAP = 600;

/** Search radius (km) applied when a destination is picked (WI #3197). Was 50 everywhere: for an
 *  Umrah/city search that pulled 450 candidates around Mecca, most of them nowhere near the
 *  Haram, and made "Search this area" meaningless (same 50 km whatever the zoom). The radius
 *  decides how many hotels exist beyond the first page, NOT how fast the first page comes back
 *  (the BE prices distance-sorted chunks until the page is full either way). */
export const HOTEL_SEARCH_DEFAULT_RADIUS_KM = 10;
/* 10, not 5 (WI #3206): measured on prod 2026-09-03 around the Haram, Amadeus returns 12 hotels
 * within 5 km but 435 within 10 km (437 at 20-30 km) — most of Makkah's inventory is geocoded
 * 5-10 km out, so 5 km showed a handful of hotels and looked like an empty city. */
/** Bounds for the viewport-derived radius on map pan/zoom (see viewport-radius.ts). Min 2 km: the
 *  BE/connector geo caches bucket lat/lng to ~1.1 km, which must stay a small fraction of the
 *  radius or a pan of a few hundred metres could be served a candidate list centred almost a
 *  bucket away (SearchHashing.COORDINATE_BUCKET_DECIMALS rationale). Max 50 km = the old default. */
export const HOTEL_AREA_RADIUS_MIN_KM = 2;
export const HOTEL_AREA_RADIUS_MAX_KM = 50;

/** Thrown by FlightSearchHotelSourcing.book() when the cart holds more than one distinct
 *  rate-plan line — be-flight-reservation-service's POST /hotels/reservations books exactly one
 *  cacheId (room type + rate plan) per call, unlike Extranet's multi-line /extranet/reservations.
 *  Looping N calls for N lines is a deliberate fast-follow, not done here (partial-failure
 *  handling — line 2 of 3 failing after line 1 was already charged — needs its own design). */
export class MultiLineNotSupportedError extends Error {}

/** Neutral "no photo available" placeholder — used instead of a real photo when a hotel/room has
 *  none uploaded. Deliberately not a stock photo: a random unrelated image (e.g. picsum.photos)
 *  reads as a real photo of the property, which is misleading. */
const NO_PHOTO_PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 520">
    <rect width="800" height="520" fill="#e2e5ea"/>
    <g fill="none" stroke="#94a3b8" stroke-width="10" stroke-linejoin="round" stroke-linecap="round">
      <rect x="300" y="190" width="200" height="140" rx="10"/>
      <circle cx="345" cy="230" r="11" fill="#94a3b8" stroke="none"/>
      <path d="M300 310 L350 260 L390 300 L420 270 L500 320"/>
    </g>
    <text x="400" y="380" text-anchor="middle" font-family="sans-serif" font-size="20" fill="#94a3b8">No photo available</text>
  </svg>`);

/** Swappable sourcing seam. MVP → ExtranetHotelSourcing. Phase 2 → OrchestratorHotelSourcing.
 *  Components depend ONLY on this token + domain models, never on an endpoint. */
export abstract class HotelSourcingService {
  /** `page` is 1-based and defaults to 1 — pagination is fetch-session state the caller owns
   *  (search.component.ts's "Load more"), never part of the search intent itself. */
  abstract search(c: HotelSearchCriteria, page?: number): Observable<HotelSearchResult>;
  /** Hotel-name autocomplete over the area of `c` (WI #3267): the names of every hotel the search
   *  will ever page through, not just the pages loaded so far. `c` must be the criteria the search
   *  actually ran with — same area/stay/sources — so the BE serves it from the candidate list that
   *  search already built (typing never triggers a provider call of its own). Filters (stars/
   *  board/refundable/price) are deliberately not sent: a name lookup must find the hotel whatever
   *  the filter rail says. Never errors: a failure degrades to no rows. */
  abstract suggestHotelNames(c: HotelSearchCriteria, q: string, limit?: number): Observable<HotelNameSuggestion[]>;
  /** The hotels `hotelIds` of the area of `c`, priced NOW under `c` — what a suggestion pick calls
   *  when the hotel has not been reached by the progressive load yet. Same result shape as a page
   *  (a hotel with no rate for the stay is absent), and the sourcing keeps them alongside the
   *  search's own hotels so a later getOffersForHotel(id) resolves them like any other. Unlike
   *  suggestHotelNames() this DOES error on failure: the caller tells "no rate" from "could not
   *  ask" apart. */
  abstract searchByHotelIds(c: HotelSearchCriteria, hotelIds: string[]): Observable<HotelOffer[]>;
  abstract getOffer(id: string, crit?: HotelSearchCriteria): Observable<HotelOffer | undefined>;
  /** Fresh offer board for ONE already-known hotel under NEW criteria (offer-detail's "Edit
   *  search" — user changed dates/guests while looking at a specific hotel). Unlike getOffer(),
   *  which only reprices/patches whatever was already cached, this rebuilds the room/plan tree
   *  from scratch so a genuinely different date range can show different rooms, not just
   *  different prices on the same rooms. */
  abstract getOffersForHotel(id: string, crit: HotelSearchCriteria): Observable<HotelOffer | undefined>;
  /** Adopts a result list the caller did NOT obtain from search() — a sessionStorage restore or
   *  an area-cache hit — into whatever per-hotel state this implementation keeps, so a later
   *  getOffersForHotel(id) can still resolve the hotel (WI #3134: those two paths repainted the
   *  list from cache while the in-memory map stayed empty, and every card then opened on
   *  "This hotel just sold out"). Purely additive: it must never evict a fresher entry. */
  abstract seedSearchCache(hotels: HotelOffer[]): void;
  /** Final, real-time-verified pricing/availability check for the exact lines about to be
   *  booked — called once, from book(), right before navigating to checkout; never automatically
   *  on page load (WI #1108: the previous automatic reprice-on-load raced the page's own
   *  unfiltered offer fetch and could silently drop a real offer). `nights` lets an Amadeus
   *  implementation recompute nightly/total from the confirmed offer's real total. No-op
   *  passthrough for Extranet — its pricing is already live/DB-authoritative at search time, no
   *  separate confirmation step exists. Amadeus lines are repriced via Step-2 and, on failure,
   *  throw OfferExpiredError/OfferUnavailableError — a hard error here is safer than silently
   *  proceeding to book a substituted offer, which is what the old REPICK fallback used to do. */
  abstract confirmLines(lines: SelectedRate[], nights: number): Observable<SelectedRate[]>;
  abstract book(req: BookingRequest): Observable<Reservation>;
  /** Prices and persists a DRAFT reservation — no provider booking, no inventory consume, no
   *  wallet debit. The drafted price is authoritative for what will be charged; the real booking
   *  happens later via confirmDraft(). Same request shape and provider dispatch as book(). */
  abstract draft(req: BookingRequest): Observable<Reservation>;
  abstract listReservations(): Observable<Reservation[]>;
  // `source` is required, not guessed: every reservation has one, callers always know it (router
  // state on navigation, a `source` query param on cold reload — see reservation-detail.component.ts)
  // and it dispatches directly to the owning backend rather than probing one then the other.
  /** Single reservation by id — backs the standalone reservation page's deep-link/reload path. */
  abstract getReservation(id: string, source: Source): Observable<Reservation | undefined>;
  /** Queues a re-send of the booking-confirmation email to the agency. */
  abstract resendConfirmation(id: string, source: Source): Observable<void>;
  // `source` disambiguates which backend owns this reservation id — both booking backends mint
  // their own UUIDs from the same namespace, so the id alone can't tell them apart. Callers always
  // have a full Reservation in hand (list/detail) and can pass its `.source` straight through.
  abstract cancel(id: string, source: Source): Observable<CancellationResult>;
  /** Executes the real provider booking (Amadeus book / Extranet inventory consume — the BE
   *  routes by the reservation's own provider) for a DRAFT reservation, moving it to BOOKED.
   *  Idempotent server-side. A provider failure surfaces in-band (BOOKING_FAILED / IN_DOUBT in the
   *  response) only for a B2C request; for B2B it is an error (409/503/504) after which the row may
   *  be BOOKING_FAILED or in doubt, so callers reload it (reservations list; reservation page, WI 3964). */
  abstract confirmDraft(id: string, source: Source): Observable<Reservation>;
  /** Read-only comparison board for the back-office review CTA: the drafted offer next to what
   *  is bookable right now for the same hotel and stay. Nothing is mutated by fetching it. */
  abstract freshOffers(id: string, source: Source): Observable<FreshOffersBoard>;
  /** Books an EXPLICITLY chosen offer from the fresh board instead of the drafted one — possibly
   *  on the OTHER provider (cross-source re-sourcing, WI #3117). The shown total travels as the
   *  consent guard (the BE refuses if the reprice exceeds it). */
  abstract confirmWithOffer(id: string, choice: FreshOfferChoice, source: Source): Observable<Reservation>;
}

export const HOTEL_SOURCING = new InjectionToken<HotelSourcingService>('HOTEL_SOURCING');

/** MVP implementation backed by be-hotel-extranet-service. Real HTTP calls only — no mock path. */
@Injectable({ providedIn: 'root' })
export class ExtranetHotelSourcing extends HotelSourcingService {
  private base = environment.apiExtranetUrl;
  private wallet = inject(WalletService);
  private auth = inject(AuthService);
  private language = inject(LanguageService);

  constructor(private http: HttpClient) { super(); }

  /** No name index on the Extranet endpoint — interface parity only (see search() below for why
   *  this implementation is not reachable today). */
  suggestHotelNames(_c: HotelSearchCriteria, _q: string, _limit?: number): Observable<HotelNameSuggestion[]> {
    return of([]);
  }
  searchByHotelIds(_c: HotelSearchCriteria, _hotelIds: string[]): Observable<HotelOffer[]> {
    return of([]);
  }

  search(c: HotelSearchCriteria, _page?: number): Observable<HotelSearchResult> {
    // Real call to be-hotel-extranet-service (proxied → :8085 to avoid CORS in dev). Not
    // reachable via HOTEL_SOURCING today (see app.config.ts) and this endpoint has no
    // page/pageSize concept server-side, so `_page` is accepted for interface parity only —
    // every call returns its one, unpaged result set as if it were the only/last page.
    const body = {
      checkIn: c.checkIn, checkOut: c.checkOut, rooms: c.rooms, adults: c.adults,
      // Geo-proximity search: the Extranet returns properties within `radiusKm` of the
      // city coordinates resolved by the city-search lookup. cityCode/city are still
      // carried for logging and the Phase-2 Amadeus connector / server-side fallback.
      latitude: c.lat ?? null, longitude: c.lng ?? null, radiusKm: c.radiusKm ?? HOTEL_SEARCH_DEFAULT_RADIUS_KM,
      cityCode: c.destCode, city: c.dest, agencyId: null, includeMarketplace: true,
      // Read live off the header switchers, not baked into HotelSearchCriteria — a currency/
      // language change applies on the next call without needing a dedicated re-search trigger.
      currency: this.wallet.currency(), language: this.language.lang(),
    };
    return this.http.post<ExtranetHotelDto[]>(`${this.base}/extranet/offers/search`, body).pipe(
      map(list => {
        const hotels = (list || []).map(h => mapExtranetHotel(h, c));
        return { hotels, totalCount: hotels.length };
      }),
      catchError(() => of({ hotels: [], totalCount: 0 } as HotelSearchResult)),
    );
  }

  /** Single property's live offers by id — backs the offer-detail deep-link/reload path.
   *  `crit` (checkIn/checkOut/rooms/adults) comes from the route's query params, not an
   *  in-memory search cache, so a bookmarked/shared/reloaded URL always resolves real data. */
  getOffer(id: string, crit?: HotelSearchCriteria): Observable<HotelOffer | undefined> {
    if (!crit) return of(undefined);
    const params: Record<string, string> = {
      checkIn: crit.checkIn, checkOut: crit.checkOut,
      rooms: String(crit.rooms), adults: String(crit.adults),
      currency: this.wallet.currency(), language: this.language.lang(),
    };
    return this.http.get<ExtranetHotelDto>(`${this.base}/extranet/offers/${id}`, { params }).pipe(
      map(h => mapExtranetHotel(h, crit)),
      catchError(() => of(undefined)),
    );
  }

  /** getOffer() above already re-derives live from the BE for whatever `crit` it's given — no
   *  cache to rebuild from, so this is a direct delegate. */
  getOffersForHotel(id: string, crit: HotelSearchCriteria): Observable<HotelOffer | undefined> {
    return this.getOffer(id, crit);
  }

  /** No-op: this implementation keeps no per-hotel cache to seed (see getOffersForHotel above). */
  seedSearchCache(_hotels: HotelOffer[]): void { /* nothing to seed */ }

  /** Extranet pricing is already live/DB-authoritative as of the search that produced these
   *  lines — no separate cacheId/Step-2 concept exists to re-confirm against. */
  confirmLines(lines: SelectedRate[], _nights: number): Observable<SelectedRate[]> {
    return of(lines);
  }

  book(req: BookingRequest): Observable<Reservation> {
    const lines = req.lines;
    const first = lines[0];
    const body = {
      idempotencyKey: req.idempotencyKey,
      propertyId: req.hotel.id,
      // Top-level fields mirror the first line for back-compat; the BE prices each `lines` entry.
      roomTypeId: first.roomTypeId, ratePlanId: first.ratePlanId, rooms: first.rooms,
      checkIn: req.crit.checkIn, checkOut: req.crit.checkOut,
      guestName: `${req.lead.first} ${req.lead.last}`.trim(), guestEmail: req.lead.email,
      // Attribute the reservation to the caller's real agency/identity so it is scopable on the listing (#14).
      agencyId: this.wallet.agencyId() || null, agencyName: this.auth.user().agency, agentEmail: this.auth.user().email,
      billingStreet: req.billing.street, billingCity: req.billing.city,
      billingZip: req.billing.zip, billingCountry: req.billing.country,
      lines: lines.map(l => ({ roomTypeId: l.roomTypeId, ratePlanId: l.ratePlanId, rooms: l.rooms })),
      currency: req.currency, language: this.language.lang(),
    };
    // Response already matches the Reservation shape (carries lines[]).
    return this.http.post<Reservation>(`${this.base}/extranet/reservations`, body);
  }

  listReservations(): Observable<Reservation[]> {
    return this.http.get<Reservation[]>(`${this.base}/extranet/reservations`).pipe(catchError(() => of([])));
  }

  getReservation(id: string, _source?: Source): Observable<Reservation | undefined> {
    return this.http.get<Reservation>(`${this.base}/extranet/reservations/${id}`).pipe(
      catchError(() => of(undefined)),
    );
  }

  resendConfirmation(_id?: string, _source?: Source): Observable<void> {
    return throwError(() => new Error('Confirmation resend is not available for Extranet reservations yet'));
  }

  draft(_req: BookingRequest): Observable<Reservation> {
    return throwError(() => new Error('Draft reservations are not available on the legacy Extranet path'));
  }

  confirmDraft(_id: string, _source?: Source): Observable<Reservation> {
    return throwError(() => new Error('Draft confirmation is not available on the legacy Extranet path'));
  }

  freshOffers(_id: string, _source?: Source): Observable<FreshOffersBoard> {
    return throwError(() => new Error('Offer review is not available on the legacy Extranet path'));
  }

  confirmWithOffer(_id: string, _choice: FreshOfferChoice, _source?: Source): Observable<Reservation> {
    return throwError(() => new Error('Offer review is not available on the legacy Extranet path'));
  }

  cancel(id: string, _source?: Source): Observable<CancellationResult> {
    return this.http.post<CancellationResult>(`${this.base}/extranet/reservations/${id}/cancel`, {});
  }
}

/** Refund preview from the cancellation-policy snapshot (full / first-night fee / none) —
 *  shown to the user before they confirm a cancellation; the actual cancel() call is still
 *  authoritative server-side. */
export function computeRefund(r: Reservation): { refundAmount: number; fee: number; note: string } {
  if (!r.refundable) return { refundAmount: 0, fee: r.total, note: 'Non-refundable — no refund.' };
  const past = r.deadline ? new Date(r.deadline) < TODAY : false;
  if (past) {
    const n = Math.max(1, Math.round((+new Date(r.checkOut) - +new Date(r.checkIn)) / 86400000));
    // To the currency's minor unit, not to whole units (WI 3976): fee + refund add up to the
    // charged total. exactMoney() first, so a half-cent share (x.xx5) is not tipped down by its
    // binary representation.
    const unit = 10 ** currencyFractionDigits(r.currency);
    const fee = Math.round(exactMoney((r.total / n) * unit)) / unit;
    return { refundAmount: exactMoney(r.total - fee), fee, note: 'Past free-cancellation deadline — first night charged.' };
  }
  return { refundAmount: r.total, fee: 0, note: 'Within free-cancellation window — full refund.' };
}

/* ===== be-hotel-extranet-service offers/search response (mirror of the Java DTOs) ===== */
interface ExtranetHotelDto {
  id: string; amadeusHotelId?: string; name: string;
  geoLocation?: { latitude: number; longitude: number };
  hotelAddress?: { lines: string; cityName: string; countryCode: string };
  status?: string; starRating?: number; amenities?: string[]; description?: string;
  photos?: string[];
  distanceKm?: number; offers?: ExtranetOfferDto[];
}
interface ExtranetOfferDto {
  id: string; amadeusOfferId?: string; checkInDate?: string; checkOutDate?: string; roomQuantity?: string;
  roomDetails?: { code?: string; name?: string; bedConfig?: string; maxOccupancy?: number;
    sizeSqm?: number; description?: string; photos?: string[]; highlights?: string[]; amenities?: string[]; beds?: string; view?: string };
  price?: { total?: number; base?: number; currency?: string };
  // Reservation-currency pair — same contract as AmadeusOfferDto's below (this legacy direct-
  // Extranet path isn't wired via HOTEL_SOURCING today, mapped for parity so it can't regress).
  reservationTotal?: number | null;
  reservationBasePrice?: number | null;
  reservationTaxes?: number | null;
  reservationCurrency?: string | null;
  mealPlan?: string; paymentPolicy?: string; hotelHasAvailableOffers?: boolean;
  policyDetails?: { type?: string; description?: string }[];
  ratePlanId?: string; roomTypeId?: string;
  // Resolved connector-side (WI #1098) — see AmadeusOfferDto below for the shared shape/meaning.
  cancellationDeadlineDate?: string | null;
  roomsAvailable?: number | null;
}

const MEAL_TO_BOARD: Record<string, Board> = {
  ROOM_ONLY: 'RO', BREAKFAST: 'BB', HALF_BOARD: 'HB', FULL_BOARD: 'FB', ALL_INCLUSIVE: 'AI',
  // Amadeus connector MealPlan variants (see the connector's MealPlan enum) — all breakfast-like.
  BUFFET_BREAKFAST: 'BB', CONTINENTAL_BREAKFAST: 'BB', ENGLISH_BREAKFAST: 'BB', CARIBBEAN_BREAKFAST: 'BB',
};

/** Reverse of MEAL_TO_BOARD's grouping — the ONE representative wire mealPlan value picked per FE
 *  Board bucket, for be-flight-search-service's v2 `boardType` request param. That param is a
 *  single string matched via `offer.mealPlan().equalsIgnoreCase(boardType)` server-side
 *  (HotelPostMergeFilter, be-flight-search-service) — an exact one-value match, not "one of a
 *  set" — so it can only ever represent ONE of MEAL_TO_BOARD's several wire values per FE bucket.
 *  'BB' picks 'BREAKFAST' (the first/most common of its four wire synonyms above); a real offer
 *  whose mealPlan is BUFFET_BREAKFAST/CONTINENTAL_BREAKFAST/ENGLISH_BREAKFAST/CARIBBEAN_BREAKFAST
 *  would be wrongly excluded by a "BB" filter today — a genuine BE contract gap (the filter would
 *  need to accept a list of wire values to match the FE's own coarser Board taxonomy), flagged
 *  here rather than silently worked around. */
const BOARD_TO_MEALPLAN: Record<Board, string> = {
  RO: 'ROOM_ONLY', BB: 'BREAKFAST', HB: 'HALF_BOARD', FB: 'FULL_BOARD', AI: 'ALL_INCLUSIVE',
};

/** Amadeus implementation backed by be-flight-search-service's /hotels/search/by-geo + reprice
 *  endpoints (com.hilbertech.ota.hotel package). Geo search only — cityCode is never sent; the
 *  city/destination picker always resolves lat/lng alongside the IATA code (see
 *  location.service.ts's dedupeByCity(), which drops any row without usable coordinates), and
 *  search.component.ts's run() already gates the search on lat/lng, not destCode. Phase-3: when
 *  be-flight-search-service's merge is enabled, a hotel's offers can carry a mix of AMADEUS and
 *  EXTRANET `source` — this class is now also a facade that delegates book()/cancel()/getOffer()
 *  to ExtranetHotelSourcing per the SELECTED LINE's source, so components keep depending only on
 *  the one HOTEL_SOURCING token (see app.config.ts) with no DI changes at the call sites. */
@Injectable({ providedIn: 'root' })
export class FlightSearchHotelSourcing extends HotelSourcingService {
  private base = environment.apiFlightSearchUrl;
  // Booking/cancel live on a DIFFERENT backend than search/reprice: be-flight-reservation-service
  // (the same service behind flight-fusion's flight bookings), which reprices the cached offer,
  // checks + debits the agency wallet, and books via the Amadeus connector — all server-side.
  private reservationBase = environment.apiFlightReservationUrl;
  private auth = inject(AuthService);
  private wallet = inject(WalletService);
  private language = inject(LanguageService);

  // Last search's hotels, keyed by hotel id. offer-detail navigates with only a hotel id
  // (see SearchComponent.open()), so getOffer() needs this to recover the cacheId per rate
  // plan for reprice. Unlike ExtranetHotelSourcing, this can't be recomputed from scratch on a
  // cold reload/deep link — an Amadeus offer only exists within the connector's cache TTL, and
  // there is no "recompute by hotel id" fallback. A reload after this map is gone resolves to
  // `undefined`, same contract as Extranet's getOffer() when `crit` is missing.
  private lastSearch = new Map<string, HotelOffer>();

  constructor(private http: HttpClient) { super(); }

  private static readonly PAGE_SIZE = HOTEL_SEARCH_PAGE_SIZE;

  /** v2 ("server-side filters") endpoint — additive alongside (never falling back to) v1's
   *  `/hotels/search/by-geo`, per be-flight-search-service's `HotelSearchResource`: stars/board/
   *  refundable are now real request params instead of a client-side predicate over an
   *  already-fetched page (see search.component.ts). */
  /** The area, stay, currency/language and sources of a search — the parameters that select the
   *  BE's cached candidate list (SearchHashing.listHashV2). Shared by search(), suggestHotelNames()
   *  and searchByHotelIds() so the three always describe the SAME search: a suggest or a pin built
   *  from anything else would make the BE build (and price) a second candidate list. */
  private areaAndStayParams(c: HotelSearchCriteria): HttpParams {
    let params = new HttpParams()
      .set('latitude', String(c.lat))
      .set('longitude', String(c.lng))
      .set('radius', String(c.radiusKm ?? HOTEL_SEARCH_DEFAULT_RADIUS_KM))
      .set('checkInDate', c.checkIn)
      .set('checkOutDate', c.checkOut)
      .set('adults', String(c.adults))
      .set('roomQuantity', String(c.rooms))
      .set('currency', this.wallet.currency())
      .set('lang', this.language.lang());
    // `sources` — repeated query param, same shape as `ratings` in search(). Undefined/empty means
    // "every source", so it's simply omitted rather than sent as an explicit all-3 list — mirrors
    // HotelSearchCriteria#sources()'s null/empty-means-all convention server-side.
    (c.sources ?? []).forEach(s => { params = params.append('sources', s); });
    return params;
  }

  /** See the interface javadoc. A query under 2 characters is answered locally (the BE rejects
   *  it with a 400 for the same reason: the dropdown would list half the area). */
  suggestHotelNames(c: HotelSearchCriteria, q: string, limit = 8): Observable<HotelNameSuggestion[]> {
    const query = (q ?? '').trim();
    if (c.lat == null || c.lng == null || query.length < 2) return of([]);
    const params = this.areaAndStayParams(c).set('q', query).set('limit', String(limit));
    return this.http.get<{ suggestions?: HotelNameSuggestion[] }>(`${this.base}/hotels/v2/suggest`, { params }).pipe(
      map(res => (res?.suggestions ?? []).filter(s => !!s?.hotelId && !!s?.name)),
      catchError(() => of([] as HotelNameSuggestion[])),
    );
  }

  /** See the interface javadoc. The same request as a page, restricted to `hotelIds` (the BE
   *  prices only those candidates — one small price-by-ids call — and answers in the page shape).
   *  Deliberately NOT going through search(): that clears the per-hotel map on page 1, and a pin
   *  must join the search's hotels, not replace them. */
  searchByHotelIds(c: HotelSearchCriteria, hotelIds: string[]): Observable<HotelOffer[]> {
    const ids = (hotelIds ?? []).filter(id => !!id);
    if (c.lat == null || c.lng == null || ids.length === 0) return of([]);
    let params = this.areaAndStayParams(c)
      .set('pageNumber', '1')
      .set('pageSize', String(Math.max(1, ids.length)));
    ids.forEach(id => { params = params.append('hotelIds', id); });
    return this.http.get<AmadeusSearchResponseDto>(`${this.base}/hotels/v2/search/by-geo`, { params }).pipe(
      map(res => {
        const hotels = res.status === 'COMPLETED'
          ? (res.hotels || []).map(h => mapAmadeusHotel(h, c)).filter(h => h.rooms.length > 0)
          : [];
        hotels.forEach(h => this.lastSearch.set(h.id, h));
        return hotels;
      }),
    );
  }

  search(c: HotelSearchCriteria, page: number = 1): Observable<HotelSearchResult> {
    if (c.lat == null || c.lng == null) return of({ hotels: [], totalCount: 0 }); // nothing resolved yet — same precondition search.component.ts's run() already enforces.
    let params = this.areaAndStayParams(c)
      .set('pageNumber', String(page))
      .set('pageSize', String(FlightSearchHotelSourcing.PAGE_SIZE));
    // `ratings` — repeated query param, one plain numeric string per selected star rating (matches
    // HotelCandidateResolutionService.applyStarsFilter's `String.valueOf(candidate.starRating())`
    // comparison exactly — NOT an enum name like "THREE_STAR").
    (c.stars ?? []).forEach(s => { params = params.append('ratings', String(s)); });
    // `boardType` — single wire value, see BOARD_TO_MEALPLAN's javadoc for why only one Board can
    // ever be forwarded here.
    if (c.boardType) params = params.set('boardType', BOARD_TO_MEALPLAN[c.boardType]);
    if (c.refundable != null) params = params.set('refundable', String(c.refundable));
    if (c.minPrice != null) params = params.set('minPrice', String(c.minPrice));
    if (c.maxPrice != null) params = params.set('maxPrice', String(c.maxPrice));
    return this.http.get<AmadeusSearchResponseDto>(`${this.base}/hotels/v2/search/by-geo`, { params }).pipe(
      map(res => {
        // The connector returns hotels with zero offers (sold out / no rate for these dates) —
        // Extranet's own search already drops these server-side (ExtranetOfferService: "if
        // (offers.isEmpty()) continue;"); mirror that here since Amadeus's response doesn't.
        // Without this, lowestNightly()/total() run Math.min() on an empty array (-> Infinity)
        // and render "€∞" in the results list.
        const hotels = res.status === 'COMPLETED'
          ? (res.hotels || []).map(h => mapAmadeusHotel(h, c)).filter(h => h.rooms.length > 0)
          : [];
        // page 1 always means "start of a fresh result set" (run()/onAreaChanged() never call
        // with any other page) — clear the old destination's cache instead of leaking it forever.
        // A later page ("Load more") accumulates into it instead, so earlier pages' hotels stay
        // repriceable via getOffer() while still on screen.
        if (page === 1) this.lastSearch.clear();
        hotels.forEach(h => this.lastSearch.set(h.id, h));
        return {
          hotels, totalCount: res.totalCount ?? hotels.length,
          hasMore: res.hasMore, totalCountIsExact: res.totalCountIsExact,
          priceRange: res.priceRangeMin != null && res.priceRangeMax != null
            ? { min: res.priceRangeMin, max: res.priceRangeMax } : undefined,
          querySources: res.querySources as SearchSource[] | undefined,
        };
      }),
      catchError(() => of({ hotels: [], totalCount: 0 } as HotelSearchResult)),
    );
  }

  /** WI #1108: no longer reprices or auto-substitutes (see confirmLines() for the real,
   *  book-time-only Step-2 confirmation this used to do eagerly on page load, which raced the
   *  page's own unfiltered offer fetch and could silently drop a real offer). Nothing in this
   *  app calls this anymore — offer-detail.component.ts's constructor calls getOffersForHotel()
   *  directly — but it stays on the interface for ExtranetHotelSourcing, which still needs it.
   *  A trivial cache read satisfies the contract without the risk the old body carried. */
  getOffer(id: string, _crit?: HotelSearchCriteria): Observable<HotelOffer | undefined> {
    return of(this.lastSearch.get(id));
  }

  /** See the interface javadoc. Additive on purpose — no clear() — so a restore can never evict
   *  an entry a live search just wrote; the seeded hotel is only ever used to resolve content and
   *  the refresh-request inputs, and getOffersForHotel() overwrites it with the live board before
   *  anything is priced or booked (booking itself re-resolves server-side). */
  seedSearchCache(hotels: HotelOffer[]): void {
    (hotels || []).forEach(h => {
      if (h?.id) this.lastSearch.set(h.id, h);
    });
  }

  /** Reprices exactly the lines the user selected, right before checkout — see the interface
   *  javadoc for why this replaces the old eager reprice-on-load. bookAmadeus() only ever sends a
   *  single-line Amadeus cart (MultiLineNotSupportedError otherwise), so in practice this fires at
   *  most one HTTP call; looped generically in case that constraint ever relaxes. Extranet lines in
   *  a (never actually mixed, see book()) cart pass through untouched. */
  confirmLines(lines: SelectedRate[], nights: number): Observable<SelectedRate[]> {
    const amadeusLines = lines.filter(l => l.source === 'AMADEUS');
    if (amadeusLines.length === 0) return of(lines); // pure-Extranet cart, nothing to confirm
    return forkJoin(amadeusLines.map(l => this.confirmOneLine(l, nights))).pipe(
      map(confirmed => {
        const byRatePlanId = new Map(confirmed.map(l => [l.ratePlanId, l]));
        return lines.map(l => byRatePlanId.get(l.ratePlanId) ?? l);
      }),
    );
  }

  private confirmOneLine(line: SelectedRate, nights: number): Observable<SelectedRate> {
    if (!line.cacheId) {
      return throwError(() => new OfferExpiredError('Missing cacheId — reprice the offer before booking'));
    }
    // `currency` is what makes this the agent's real final price. It was omitted here on the
    // belief that the reprice endpoint was cache-bound to the original search currency; it is
    // not — be-flight-search-service converts on the way out, exactly like /offers/refresh, and
    // needs to be told what to convert toward. Without it the response came back with
    // reservationTotal null, which this method reads (correctly) as "un-priceable" — so checkout
    // blocked every Amadeus booking, in every currency. See the three-state contract on
    // RatePlan.resNightly: null has to mean the FX engine failed, never that nobody asked.
    const priceParams = new HttpParams()
      .set('currency', this.wallet.currency())
      .set('lang', this.language.lang());
    return this.http.get<AmadeusRepriceResponseDto>(
      `${this.base}/hotels/offers/${line.cacheId}/price`, { params: priceParams }).pipe(
      map(res => {
        const offers = res.hotelOffers?.length ? res.hotelOffers : (res.offer ? [res.offer] : []);
        const fresh = offers.find(o => o.offerId === line.ratePlanId) ?? offers[0];
        // Unlike the old getOffer(), a Step-2 response with no usable offer is a hard failure
        // here, not "keep showing the indicative price" — this IS the user's final confirmation
        // before booking, so a stale/wrong price must never reach checkout silently.
        if (!fresh) throw new OfferExpiredError('Reprice returned no offers');
        const total = Number(fresh.totalPrice ?? 0);
        // Reservation-currency pair refreshed alongside the native price (feature:
        // hotel-reservation-currency). A reprice response that carries NO reservation fields
        // keeps the line's previous res* values (undefined must not clobber a real figure);
        // an explicit null propagates — this IS the final pre-booking confirmation, so a
        // now-un-priceable offer must reach checkout blocked (§4), never silently priced.
        const keepRes = fresh.reservationTotal === undefined;
        const repriced: SelectedRate = {
          ...line,
          nightly: perNight(total, nights), stayTotal: total,
          resNightly: keepRes ? line.resNightly : deriveResNightly(fresh.reservationTotal, nights),
          resStayTotal: keepRes ? line.resStayTotal : resStay(fresh.reservationTotal),
        };
        return {
          // Line totals off the confirmed per-room STAY totals × rooms (WI 3976) — the BE's own
          // debit formula — never off a per-night figure × nights.
          ...repriced, cacheId: fresh.cacheId, ...lineTotalsOf(repriced, nights, line.rooms),
          // The reprice mints a NEW bookingOfferId server-side (an identity row for the fresh
          // cacheId) — the line's old UUID would resolve to the STALE cacheId, so it is replaced,
          // not kept. Deliberately unconditional: a response from a BE without the identity store
          // clears it to undefined, which routes booking through the legacy cacheId fallback —
          // and that carries the fresh cacheId swapped on this same line.
          bookingOfferId: fresh.bookingOfferId,
          resCurrency: fresh.reservationCurrency ?? line.resCurrency,
        };
      }),
      catchError((err: unknown) => {
        if (err instanceof OfferExpiredError || err instanceof OfferUnavailableError) return throwError(() => err);
        const status = (err as HttpErrorResponse)?.status;
        if (status === 410) return throwError(() => new OfferExpiredError('Offer expired, re-search required'));
        if (status === 409) return throwError(() => new OfferUnavailableError('Hotel sold out'));
        return throwError(() => err); // unexpected failure — surface it, never silently proceed to book a stale price
      }),
    );
  }

  /** offer-detail's "Edit search": the user picked NEW dates/guests while looking at one known
   *  hotel. Unlike confirmLines() above (which only patches prices onto lines already selected
   *  from the ORIGINAL search), this calls the same
   *  /offers/refresh endpoint but REBUILDS the tree via groupOffersIntoRooms() from the fresh
   *  response — new dates can mean genuinely different rooms/rate-plan ids, not just different
   *  prices on the same ones, which patch-by-ratePlanId can never surface. `roomDetails` is
   *  synthesized from the already-cached rooms (this endpoint returns no hotel content) so
   *  Amadeus rooms keep their resolved friendly name instead of falling back to the raw
   *  provider code. */
  getOffersForHotel(id: string, crit: HotelSearchCriteria): Observable<HotelOffer | undefined> {
    const cached = this.lastSearch.get(id);
    if (!cached) return of(undefined); // same cold-reload contract as getOffer() above
    const extranetPropertyId = cached.rooms.flatMap(r => r.plans).find(p => p.source === 'EXTRANET')?.extranetPropertyId;
    const roomDetails = cached.rooms.map(r => ({ providerReferenceId: r.roomTypeId, name: r.room }));

    let params = new HttpParams()
      .set('checkInDate', crit.checkIn)
      .set('checkOutDate', crit.checkOut)
      .set('adults', String(crit.adults))
      .set('roomQuantity', String(crit.rooms))
      .set('currency', this.wallet.currency())
      .set('lang', this.language.lang());
    if (extranetPropertyId) params = params.set('extranetPropertyId', extranetPropertyId);
    // `sources` — repeated query param, same shape/convention as search()'s own (below). Undefined/
    // empty means "every source", matching HotelOffersRefreshResponseDto#querySources' null/empty-
    // means-all contract server-side.
    (crit.sources ?? []).forEach(s => { params = params.append('sources', s); });

    return this.http.get<AmadeusOffersRefreshResponseDto>(`${this.base}/hotels/${id}/offers/refresh`, { params }).pipe(
      map(res => {
        const rooms = roomsFromRefreshResponse(res.rooms || [], crit, roomDetails);
        const fresh: HotelOffer = { ...cached, rooms, querySources: res.querySources };
        this.lastSearch.set(id, fresh); // so a later reprice/booking picks up the fresh cacheIds, not stale ones
        return fresh;
      }),
      catchError(() => of(cached)), // transient failure — fall back to the pre-edit state rather than blank the page
    );
  }

  /** Every line books through this one flight-reservation endpoint now — the source-based facade
   *  dispatch to ExtranetHotelSourcing is retired (T-B6). Amadeus keeps its hard backend
   *  constraint of one cacheId (room type + rate plan) per call (MultiLineNotSupportedError);
   *  Extranet, unlike Amadeus, keeps its existing multi-line-per-reservation capability — a cart
   *  with several Extranet room-type/rate-plan lines is sent as one call with an `extranetLines`
   *  array. A mixed Amadeus+Extranet cart isn't supported (falls to the one-line check below, same
   *  as today). checkout.component.ts also guards pre-flight; this is defense in depth. */
  book(req: BookingRequest): Observable<Reservation> {
    return this.submitReservation(req, '/hotels/reservations');
  }

  /** Same dispatch and request shape as book(), against the /draft endpoint: the BE prices the
   *  offer and persists a DRAFT — nothing is booked, consumed or debited until confirmDraft(). */
  draft(req: BookingRequest): Observable<Reservation> {
    return this.submitReservation(req, '/hotels/reservations/draft');
  }

  private submitReservation(req: BookingRequest, path: string): Observable<Reservation> {
    const lines = req.lines;
    // PRIMARY, source-blind path (feat: hotel-booking-identity-source-blind): every selected line
    // carries the bookingOfferId minted by be-flight-search-service, so identity travels as ONE
    // offerSelections body and be-flight-reservation-service resolves each UUID
    // (GET {search}/hotels/booking-offers/{id}) and routes Amadeus vs Extranet itself — the FE
    // never branches on `source` here (that field stays display-only).
    if (lines.length > 0 && lines.every(l => !!l.bookingOfferId)) {
      return this.submitByOfferSelections(req, path);
    }
    // legacy identity fallback — remove once bookingOfferId is everywhere (a line without the
    // UUID can only come from an old sessionStorage snapshot or a pre-rollout search response).
    const allExtranet = lines.length > 0 && lines.every(l => l.source === 'EXTRANET');
    if (allExtranet) {
      return this.bookExtranetViaOrchestrator(req, path);
    }
    if (lines.length !== 1) {
      return throwError(() => new MultiLineNotSupportedError(
        'Amadeus bookings support a single room type per reservation'));
    }
    return this.bookAmadeus(req, lines[0], path);
  }

  /** Source-blind request builder: one body for ANY cart whose lines all carry a bookingOfferId —
   *  no cacheId/extranetPropertyId/extranetLines/checkIn/checkOut here, the resolver row behind
   *  each UUID already holds them server-side (`rooms` is the per-line override the B2B cart
   *  always knows). Guest collection is unchanged from the legacy builders: the lead repeated
   *  once per room, roomIndex 0..total-1, first one lead. */
  private submitByOfferSelections(req: BookingRequest, path: string): Observable<Reservation> {
    const lines = req.lines;
    const totalRooms = lines.reduce((sum, l) => sum + l.rooms, 0);
    const guests: FlightResHotelGuestDto[] = Array.from({ length: totalRooms }, (_, i) => ({
      title: req.lead.title, firstName: req.lead.first, lastName: req.lead.last,
      email: req.lead.email, phone: req.lead.phone, roomIndex: i, lead: i === 0,
    }));
    const body: FlightResReservationRequestDto = {
      offerSelections: lines.map(l => ({ bookingOfferId: l.bookingOfferId as string, rooms: l.rooms })),
      guests,
      currency: req.currency, language: this.language.lang(),
      hotelSnapshot: {
        name: req.hotel.name || null,
        address: req.hotel.address || null,
        stars: req.hotel.stars || null,
        latitude: req.hotel.lat || null,
        longitude: req.hotel.lng || null,
      },
      billingAddress: {
        firstName: req.lead.first, lastName: req.lead.last, email: req.lead.email,
        street: req.billing.street, city: req.billing.city,
        postalCode: req.billing.zip, countryCode: req.billing.country,
      },
    };
    return this.http.post<FlightResReservationResponseDto>(`${this.reservationBase}${path}`, body).pipe(
      map(res => this.mapReservation(res, req)),
      catchError((err: HttpErrorResponse) => {
        // FRE is authoritative on this path: 410 = the bookingOfferId is unknown or older than
        // the resolver's validity window (24h default — re-search required); 409 = resolved fine
        // but no longer bookable (sold out / price no longer confirmable); 400 = the selections
        // aren't combinable in one reservation server-side (mixed sources / multi-Amadeus) — the
        // same rule checkout.component.ts's blockedMultiLine() only pre-flights as UX.
        if (err.status === 410) return throwError(() => new OfferExpiredError('Offer expired, re-search required'));
        if (err.status === 409) return throwError(() => new OfferUnavailableError('Room no longer available'));
        if (err.status === 400) return throwError(() => new MultiLineNotSupportedError(
          'Selected lines cannot be combined in one reservation'));
        return throwError(() => err);
      }),
    );
  }

  confirmDraft(id: string, _source: Source): Observable<Reservation> {
    return this.http.post<FlightResReservationResponseDto>(
      `${this.reservationBase}/hotels/reservations/${id}/confirm`, {}).pipe(
      map(res => this.mapListedReservation(res)),
    );
  }

  freshOffers(id: string, _source: Source): Observable<FreshOffersBoard> {
    return this.http.get<FreshOffersBoard>(
      `${this.reservationBase}/hotels/reservations/${id}/fresh-offers`);
  }

  confirmWithOffer(id: string, choice: FreshOfferChoice, _source: Source): Observable<Reservation> {
    return this.http.post<FlightResReservationResponseDto>(
      `${this.reservationBase}/hotels/reservations/${id}/confirm-with-offer`,
      choice).pipe(
      map(res => this.mapListedReservation(res)),
    );
  }

  // legacy identity fallback — remove once bookingOfferId is everywhere.
  private bookAmadeus(req: BookingRequest, line: SelectedRate, path: string): Observable<Reservation> {
    if (!line.cacheId) {
      return throwError(() => new OfferExpiredError('Missing cacheId — reprice the offer before booking'));
    }
    // The BE requires every roomIndex 0..roomCount-1 to be covered with exactly one lead each;
    // per-room guest collection is a fast-follow, so the lead guest is repeated per room.
    const roomCount = line.rooms;
    const guests: FlightResHotelGuestDto[] = Array.from({ length: roomCount }, (_, i) => ({
      title: req.lead.title, firstName: req.lead.first, lastName: req.lead.last,
      email: req.lead.email, phone: req.lead.phone, roomIndex: i, lead: i === 0,
    }));
    // No idempotencyKey here (unlike Extranet): the BE mints its own per-reservation key for the
    // connector call. No hotelSource either — that enum is an Amadeus source FILTER
    // (BEDBANK/DIRECTCHAIN/ALL), not a provider tag; sending 'AMADEUS' would 400.
    const body: FlightResReservationRequestDto = {
      cacheId: line.cacheId,
      // For an Amadeus (or Phase-3 merged) hotel card, hotel.id IS the Amadeus property code;
      // guard on the 8-char shape so an extranet-only id never reaches the connector's pattern
      // validation downstream.
      hotelAmadeusId: /^[A-Z0-9]{8}$/.test(req.hotel.id) ? req.hotel.id : undefined,
      roomCount,
      guests,
      currency: req.currency, language: this.language.lang(),
      // Display snapshot of what the agent is booking — the BE persists it immutably (the
      // reprice offer carries no address/stars/coordinates, and its hotelSummary name can be
      // just the property code, so the client is the authoritative display source).
      hotelSnapshot: {
        name: req.hotel.name || null,
        address: req.hotel.address || null,
        stars: req.hotel.stars || null,
        latitude: req.hotel.lat || null,
        longitude: req.hotel.lng || null,
      },
      billingAddress: {
        firstName: req.lead.first, lastName: req.lead.last, email: req.lead.email,
        street: req.billing.street, city: req.billing.city,
        postalCode: req.billing.zip, countryCode: req.billing.country,
      },
    };
    return this.http.post<FlightResReservationResponseDto>(`${this.reservationBase}${path}`, body).pipe(
      map(res => this.mapReservation(res, req)),
      catchError((err: HttpErrorResponse) => {
        // be-flight-reservation-service now distinguishes these two outcomes on this exact call
        // (WI #1110 follow-up) — this is the FINAL booking submission, so a stale/no-longer-
        // bookable offer here must surface as a clear, typed error (same OfferExpiredError/
        // OfferUnavailableError contract confirmLines() already uses one step earlier) instead of
        // the generic "booking failed" checkout.component.ts showed for every failure until now.
        if (err.status === 410) return throwError(() => new OfferExpiredError('Offer expired, re-search required'));
        if (err.status === 409) return throwError(() => new OfferUnavailableError('Room no longer available'));
        return throwError(() => err);
      }),
    );
  }

  /** extranetPropertyId comes from the selected lines' RatePlan, NOT req.hotel.id — for a Phase-3
   *  merged hotel card, hotel.id is the AMADEUS hotel id; only the RatePlan carries the real
   *  Extranet property id (see groupOffersIntoRooms/mapExtranetHotel). All lines in one booking
   *  share the same property, so the first line's is authoritative.
   *  legacy identity fallback — remove once bookingOfferId is everywhere. */
  private bookExtranetViaOrchestrator(req: BookingRequest, path: string): Observable<Reservation> {
    const lines = req.lines;
    const propertyId = lines[0].extranetPropertyId;
    if (!propertyId) {
      return throwError(() => new OfferExpiredError('Missing extranetPropertyId — reprice the offer before booking'));
    }
    const totalRooms = lines.reduce((sum, l) => sum + l.rooms, 0);
    const guests: FlightResHotelGuestDto[] = Array.from({ length: totalRooms }, (_, i) => ({
      title: req.lead.title, firstName: req.lead.first, lastName: req.lead.last,
      email: req.lead.email, phone: req.lead.phone, roomIndex: i, lead: i === 0,
    }));
    const body: FlightResReservationRequestDto = {
      roomCount: totalRooms,
      guests,
      currency: req.currency, language: this.language.lang(),
      extranetPropertyId: propertyId,
      extranetLines: lines.map(l => ({ roomTypeId: l.roomTypeId, ratePlanId: l.ratePlanId, rooms: l.rooms })),
      checkIn: req.crit.checkIn,
      checkOut: req.crit.checkOut,
      hotelSnapshot: {
        name: req.hotel.name || null,
        address: req.hotel.address || null,
        stars: req.hotel.stars || null,
        latitude: req.hotel.lat || null,
        longitude: req.hotel.lng || null,
      },
      billingAddress: {
        firstName: req.lead.first, lastName: req.lead.last, email: req.lead.email,
        street: req.billing.street, city: req.billing.city,
        postalCode: req.billing.zip, countryCode: req.billing.country,
      },
    };
    return this.http.post<FlightResReservationResponseDto>(`${this.reservationBase}${path}`, body).pipe(
      map(res => this.mapReservation(res, req)),
    );
  }

  /** All reservations — both providers — now live in be-flight-reservation-service; the forkJoin
   *  with ExtranetHotelSourcing's own /extranet/reservations listing is retired (T-B6). Pre-cutover
   *  Extranet test bookings (the old HB-2026-… references) drop off this list — accepted, they
   *  were test data. */
  listReservations(): Observable<Reservation[]> {
    const params = new HttpParams().set('pageNumber', '1').set('pageSize', '50');
    return this.http.get<FlightResPaginatedDto<FlightResReservationResponseDto>>(
      `${this.reservationBase}/hotels/reservations`, { params }).pipe(
      map(page => (page.data || []).map(res => this.mapListedReservation(res))),
      catchError(() => of([] as Reservation[])),
    );
  }

  /** Maps a listed reservation (no originating BookingRequest available, unlike mapReservation —
   *  hotel/guest/board context comes from the fields the BE snapshots at booking time). */
  private mapListedReservation(res: FlightResReservationResponseDto): Reservation {
    const user = this.auth.user();
    return {
      id: res.id,
      hotel: res.hotelName || res.hotelAmadeusId || 'Hotel',
      city: res.hotelCityCode || '',
      // Pre-cutover rows have no provider column value other than the AMADEUS default the
      // migration backfilled — defaulting here too keeps any stale/uncached response consistent.
      source: (res.provider as Source) ?? 'AMADEUS',
      status: FLIGHT_RES_STATUS_MAP[res.status] ?? 'PENDING',
      checkIn: res.checkInDate ?? '', checkOut: res.checkOutDate ?? '',
      rooms: res.roomCount ?? 1,
      board: (res.mealPlan && MEAL_TO_BOARD[res.mealPlan]) || 'RO',
      guest: res.guestName || '',
      guestEmail: res.guestEmail || undefined,
      pnr: res.pnr || undefined,
      providerOrderId: res.providerOrderId || undefined,
      providerConfirmationId: res.providerConfirmationId || undefined,
      hotelCode: res.hotelAmadeusId || undefined,
      createdAt: res.createdAt || undefined,
      refundedAt: res.refundedAt || undefined,
      address: res.hotelAddress || undefined,
      stars: res.hotelStars ?? undefined,
      lat: res.hotelLatitude != null ? Number(res.hotelLatitude) : undefined,
      lng: res.hotelLongitude != null ? Number(res.hotelLongitude) : undefined,
      photoUrl: res.hotelPhotoUrl || undefined,
      roomTypeName: res.roomTypeName || undefined,
      bedType: res.bedType || undefined,
      numberOfBeds: res.numberOfBeds ?? undefined,
      maxOccupancy: res.maxOccupancy ?? undefined,
      refundAmount: res.refundAmount ?? undefined,
      agency: user.agency, agencyEmail: user.email,
      // Only an explicit nonRefundable=false means refundable — failed bookings have no details,
      // and "!undefined" used to render them as "Free until —".
      refundable: res.nonRefundable === false,
      deadline: res.cancellationDeadline ?? null,
      // Same rule as mapReservation(): the amount the wallet paid, in the currency it paid it in,
      // falling back to the native pair only for a booking that had no reservation currency.
      total: res.reservationPrice ?? res.totalPrice ?? 0,
      currency: res.reservationCurrency ?? res.currency ?? 'EUR',
    };
  }

  /** `source` is no longer used to dispatch (both providers live behind the same endpoint now,
   *  T-B6) — kept in the signature for interface compatibility with the abstract class and
   *  ExtranetHotelSourcing, same convention that class already uses for its own unused `_source`
   *  params. See reservation-detail.component.ts for how it's carried across navigation state. */
  getReservation(id: string, _source: Source): Observable<Reservation | undefined> {
    return this.http.get<FlightResReservationResponseDto>(`${this.reservationBase}/hotels/reservations/${id}`).pipe(
      map(res => this.mapListedReservation(res)),
      catchError(() => of(undefined)),
    );
  }

  resendConfirmation(id: string, _source: Source): Observable<void> {
    // 202 from the BE means "queued on the Service Bus topic" — the notification service
    // delivers the email asynchronously.
    return this.http.post<void>(`${this.reservationBase}/hotels/reservations/${id}/resend-confirmation`, {});
  }

  cancel(id: string, _source: Source): Observable<CancellationResult> {
    return this.http.post<FlightResReservationResponseDto>(
      `${this.reservationBase}/hotels/reservations/${id}/cancel`, {}).pipe(
      map(res => {
        const refund = res.refundAmount ?? 0;
        return { status: 'CANCELLED' as const, refundAmount: refund, fee: (res.totalPrice ?? 0) - refund };
      }),
    );
  }

  /** Maps the BE reservation response into the FE Reservation model. Hotel/room/board context
   *  comes from the request (the response only carries provider/pricing data). */
  /** Handles both a single Amadeus line and multiple Extranet lines. The response carries one
   *  aggregate totalPrice (no per-line breakdown from the BE), so `lines[].lineTotal` falls back
   *  to each line's own indicative pre-booking total rather than the confirmed one — only the
   *  aggregate `total` reflects what was actually charged. */
  private mapReservation(res: FlightResReservationResponseDto, req: BookingRequest): Reservation {
    const lines = req.lines;
    const firstLine = lines[0];
    const requestedRooms = lines.reduce((sum, l) => sum + l.rooms, 0);
    // Fallback total for a response that carries no totalPrice: the amount in the RESERVATION
    // currency, which is what the wallet was debited and what §5 renders — the native sum
    // (lines[].total) is in the source's own currency under the reservation-currency contract and
    // would be a different number entirely. Falls back to the native sum only for a pre-contract
    // cart, where resGrandTotal() itself reads the native totals anyway.
    const requestedTotal = resGrandTotal(lines) ?? lines.reduce((sum, l) => sum + l.total, 0);
    const user = this.auth.user();
    return {
      id: res.id,
      hotel: req.hotel.name, city: req.hotel.city,
      source: (res.provider as Source) ?? firstLine.source,
      status: FLIGHT_RES_STATUS_MAP[res.status] ?? 'PENDING',
      checkIn: res.checkInDate ?? req.crit.checkIn, checkOut: res.checkOutDate ?? req.crit.checkOut,
      rooms: res.roomCount ?? requestedRooms, board: firstLine.board,
      guest: `${req.lead.first} ${req.lead.last}`.trim(),
      agency: user.agency, agencyEmail: user.email,
      refundable: !res.nonRefundable,
      deadline: res.cancellationDeadline ?? null,
      // The reservation pair FIRST: res.totalPrice/res.currency are the offer's NATIVE amount, in
      // the source's own currency, and rendering those told an agent who booked in USD that they
      // had been "charged SAR 2,578.02" — a currency they never chose. The BE now returns what the
      // wallet was actually debited alongside the native pair; the native one stays as the
      // fallback for a booking made without a reservation currency, where it IS what was paid.
      total: res.reservationPrice ?? res.totalPrice ?? requestedTotal,
      currency: res.reservationCurrency ?? res.currency ?? req.currency,
      lines: lines.map(l => ({
        roomTypeId: l.roomTypeId, ratePlanId: l.ratePlanId, roomName: l.room,
        board: l.board, rooms: l.rooms, lineTotal: l.total,
      })),
    };
  }
}

/* ===== be-flight-reservation-service /hotels/reservations request/response (mirror of the Java
 * DTOs; see com.hilbertech.ota.hotel.dto + HotelReservationResource in be-flight-reservation-service,
 * guest/billing shapes from the shared common lib's hotelconn package) ===== */
interface FlightResHotelGuestDto {
  title: string; firstName: string; lastName: string; email: string; phone: string;
  childAge?: number; roomIndex: number; lead: boolean;
}
interface FlightResBillingAddressDto {
  firstName: string; lastName: string; email: string; street: string; city: string;
  state?: string; postalCode: string; countryCode: string;
}
interface FlightResExtranetLineDto { roomTypeId: string; ratePlanId: string; rooms: number; }
/** One line of the source-blind primary path — mirrors OfferSelection(UUID bookingOfferId,
 *  Integer rooms) in be-flight-reservation-service's HotelReservationRequest. `rooms` is the
 *  per-line override (nullable server-side, defaulting to the resolved roomCount) — this FE
 *  always sends it, the cart knows the per-line quantity. */
interface FlightResOfferSelectionDto { bookingOfferId: string; rooms: number; }
interface FlightResReservationRequestDto {
  // PRIMARY identity (feat: hotel-booking-identity-source-blind) — exactly-one-of THREE shapes:
  // offerSelections XOR cacheId XOR extranetLines(+extranetPropertyId+checkIn/checkOut). When
  // present, the FE sends NO cacheId/extranet fields/dates/roomCount: the resolver row behind
  // each UUID already carries them, and FRE derives roomCount from the selections.
  offerSelections?: FlightResOfferSelectionDto[];
  cacheId?: string;
  // Amadeus 8-char property code — the BE persists it on the draft so an EXPIRED cacheId can be
  // refreshed at confirm time (the offer DTOs never carry the hotel code themselves).
  hotelAmadeusId?: string;
  // Required on the legacy shapes; absent on the offerSelections shape (see above).
  roomCount?: number;
  currency?: string; language?: string;
  guests: FlightResHotelGuestDto[]; billingAddress: FlightResBillingAddressDto;
  hotelSnapshot?: { name: string | null; address: string | null; stars: number | null; latitude: number | null; longitude: number | null };
  // EXTRANET only — cacheId is the AMADEUS discriminator, extranetLines the EXTRANET one (see
  // HotelReservationService.resolveProvider in be-flight-reservation-service). One or more
  // room-type/rate-plan lines per booking — Extranet, unlike Amadeus, keeps its existing
  // multi-line-per-reservation capability through this unified endpoint.
  extranetPropertyId?: string; extranetLines?: FlightResExtranetLineDto[];
  checkIn?: string; checkOut?: string;
}
type FlightResReservationStatus =
  'DRAFT' | 'BOOKING_REQUESTED' | 'PENDING_BOOKING' | 'BOOKED' | 'BOOKING_FAILED' | 'BOOKING_IN_DOUBT'
  | 'CANCELLED' | 'CANCELLATION_FAILED';

/** GET /hotels/reservations/{id}/fresh-offers — the back-office comparison board: the drafted
 *  offer as frozen on the reservation next to today's bookable candidates. sameRoom mirrors the
 *  BE confirm-time matcher's room identity: candidates without it are a re-sourcing decision. */
export interface FreshOffersBoard {
  drafted: {
    status: FlightResReservationStatus; provider?: string; hotelName?: string;
    roomTypeName?: string; roomCategory?: string; bedType?: string; ratePlanCode?: string;
    mealPlan?: string; nonRefundable?: boolean; checkInDate?: string; checkOutDate?: string;
    nights?: number; roomCount?: number; totalPrice?: number; currency?: string;
    // Reservation-currency pair frozen at draft time — the amount the wallet was (or will be)
    // charged. Absent for a draft made without a reservation currency: the board stays native.
    reservationTotal?: number; reservationCurrency?: string;
  };
  offers: FreshOfferCard[];
}
export interface FreshOfferCard {
  // Cross-source (WI #3117): AMADEUS cards carry a cacheId, EXTRANET cards carry
  // extranetPropertyId + roomTypeId + ratePlanId — the identity confirm-with-offer books.
  source?: Source;
  cacheId?: string; extranetPropertyId?: string; roomTypeId?: string; ratePlanId?: string;
  hotelName?: string;
  roomType?: string; roomCategory?: string; bedType?: string; rateCode?: string;
  mealPlan?: string; refundable?: boolean; cancellationDeadlineDate?: string;
  totalPrice: number; currency?: string; deltaVsDrafted?: number; sameRoom: boolean;
  // Native total converted by the BE with the drafted snapshot rate — the wallet never moves
  // MORE than this on booking this card (exact when the confirm reprice equals the board total).
  // Null for a candidate the drafted rate cannot convert (cross-currency: unbookable under the
  // v1 same-currency rule) — those render native, with no Book CTA.
  reservationTotal?: number; reservationDeltaVsDrafted?: number;
}

/** The operator's explicit choice sent to confirm-with-offer — exactly one identity. */
export interface FreshOfferChoice {
  cacheId?: string;
  extranetPropertyId?: string;
  extranetLines?: { roomTypeId: string; ratePlanId: string; rooms: number }[];
  expectedTotal: number;
}
interface FlightResReservationResponseDto {
  id: string; status: FlightResReservationStatus; hotelSource?: string;
  // Which connector actually fulfilled the booking — 'AMADEUS' | 'EXTRANET' (BookingProvider in
  // be-flight-reservation-service). Replaces the FE's own hardcoded 'AMADEUS' tag now that this
  // endpoint books both providers.
  provider?: string;
  providerOrderId?: string; providerBookingId?: string; providerConfirmationId?: string; pnr?: string;
  hotelAmadeusId?: string; hotelName?: string; hotelCityCode?: string; guestName?: string; guestEmail?: string; mealPlan?: string;
  checkInDate?: string; checkOutDate?: string; nights?: number; roomCount?: number;
  // NATIVE pair — the source's own currency (Amadeus: whatever the property is quoted in;
  // Extranet: the owner's). Never rendered to an agent on its own (decision A).
  totalPrice?: number; currency?: string;
  // What the agency's wallet was actually debited, in the currency it chose, at the rate locked
  // at booking. Absent for a booking made without a reservation currency.
  reservationPrice?: number; reservationCurrency?: string; exchangeRateSnapshot?: number;
  cancellationDeadline?: string | null; nonRefundable?: boolean;
  refundAmount?: number | null; createdAt?: string; refundedAt?: string;
  hotelAddress?: string; hotelStars?: number; hotelLatitude?: number; hotelLongitude?: number;
  hotelPhotoUrl?: string; roomTypeName?: string; bedType?: string; numberOfBeds?: number; maxOccupancy?: number;
}
/** com.hilbertech.portsaidtours.common.dto.shared.PaginatedResponse */
interface FlightResPaginatedDto<T> {
  data: T[]; page: number; size: number; totalRecords: number;
}

// CANCELLATION_FAILED also renders as FAILED — the FE has no dedicated status for a booking
// that is still active but whose cancellation errored; the BE keeps it cancellable.
// BOOKING_IN_DOUBT renders as PENDING: the provider outcome is unknown and under
// reconciliation — neither a failure nor a confirmed booking yet.
const FLIGHT_RES_STATUS_MAP: Record<FlightResReservationStatus, BookingStatus> = {
  DRAFT: 'DRAFT', BOOKING_REQUESTED: 'REQUESTED', PENDING_BOOKING: 'PENDING', BOOKED: 'BOOKED',
  CANCELLED: 'CANCELLED', BOOKING_FAILED: 'FAILED', CANCELLATION_FAILED: 'FAILED',
  BOOKING_IN_DOUBT: 'PENDING',
};

/** Shared refundable heuristic over free-text policy entries (non-refundable ⇒ false; otherwise
 *  any mention of cancel/refund ⇒ true). Amadeus policy entries use `policyType`, Extranet's use
 *  `type` — both accepted so the one heuristic covers hotel-wide and per-offer callers alike. */
function refundableFromPolicyText(entries?: { type?: string; policyType?: string; description?: string; policyProperties?: Record<string, unknown> }[]): boolean {
  // Structured signal first — Amadeus's OFFER_REFUNDABLE.policyProperties.cancellationRefund is
  // the same authoritative flag be-amadeus-hotel-conn-service's own booking mapper keys off of;
  // more reliable than parsing the connector's synthesized description text below, which only
  // incidentally embeds the same value as free text.
  const structured = (entries || [])
    .map(p => p.policyProperties?.['cancellationRefund'])
    .find((v): v is string => typeof v === 'string');
  if (structured) {
    return structured.toUpperCase() !== 'NON_REFUNDABLE';
  }
  const txt = (entries || []).map(p => `${p.type ?? p.policyType ?? ''} ${p.description ?? ''}`).join(' ').toLowerCase();
  // Extranet's `type` comes through as "NON_REFUNDABLE" (underscore, see ExtranetOfferService),
  // not the hyphenated "non-refundable" this used to check for alone — that mismatch meant the
  // exclusion never fired, and since "refund" is a substring of both "refundable" and
  // "non_refundable", every Extranet plan came back refundable regardless of its real policy.
  const nonRefundable = txt.includes('non-refundable') || txt.includes('non_refundable') || txt.includes('nonrefundable');
  return !nonRefundable && (txt.includes('cancel') || txt.includes('refund'));
}

/** Groups a flat (possibly Phase-3-merged) offer list into the FE's room/rate-plan tree.
 *  Extranet-sourced offers — tagged `source === 'EXTRANET'` after the be-flight-search-service
 *  merge — key by their real `extranetRoomTypeId`, since that id is what booking/refresh need
 *  downstream (a genuine DB UUID primary key, guaranteed unique). Amadeus offers have no such id
 *  at all, so they key by a composite of `roomType` + `roomCategory` + `roomBedConfig` (WI #1095 —
 *  a real live capture showed Amadeus using the SAME generic `roomType` code, e.g. "ROH"/
 *  Run-of-House, across genuinely different physical rooms — 9 offers all tagged "ROH" split
 *  evenly between STANDARD_ROOM/TWIN, ACCESSIBLE_ROOM/KING, and STANDARD_ROOM/SINGLE; keying on
 *  `roomType` alone collapsed all 9 into a single room card). This composite join is a sanctioned
 *  design choice, not a stopgap: these three fields are opaque per-provider labels the connector
 *  (be-amadeus-hotel-conn-service) deliberately does NOT resolve into a platform enum (unlike
 *  mealPlan/refundable, which have real cross-source business meaning to interpret) — nothing here
 *  requires knowing what any Amadeus value MEANS, only comparing them for equality, so combining
 *  them client-side isn't the kind of provider-interpretation the connector-normalization rule is
 *  about. The connector DOES guarantee they arrive trimmed/blank-to-null (HotelOffersMapper's
 *  normalizeLabel()), so this join never has to defensively re-clean a raw provider quirk itself.
 *  `roomCategory`/`roomBedConfig` are appended only when present, so hotels where Amadeus DOES
 *  give a specific `roomType` per room (no collision) don't get a needlessly longer key — and a
 *  hotel with many rate plans genuinely sharing one room (same roomType+category+bedType, e.g. the
 *  22-rate "ROH" case seen earlier) still buckets together, since none of the 3 components differ.
 *  Used only for the initial search mapping — getOffer()'s reprice merges fresh prices into an
 *  already-built tree instead of rebuilding it (see FlightSearchHotelSourcing.getOffer()). */
/** Room-display-metadata shell for one grouping key's first-seen offer — name fallback chain,
 *  bed/description/amenities, etc. Split out of groupOffersIntoRooms() so roomsFromRefreshResponse()
 *  (which trusts be-flight-search-service's own grouping/ordering, see HotelRoomAssembler) can
 *  build the same per-room display metadata without re-deriving a grouping key itself. */
function buildRoomShell(
  key: string, o: AmadeusOfferDto, nameByRoomType: Map<string, string>,
): HotelRoom {
  const isExtranet = o.source === 'EXTRANET';
  return {
    roomTypeId: key,
    // Name fallback chain: Extranet's real name > Amadeus content-correlated name (rarely
    // matches in practice, see nameByRoomType above) > Amadeus's own roomCategory ("STANDARD_
    // ROOM" etc., title-cased — WI #1082, be-flight-search-service now maps this too) > the
    // raw room-type code as a last resort.
    room: (isExtranet ? o.roomTypeName : (o.roomType && nameByRoomType.get(o.roomType)))
      || (o.roomCategory && titleCaseCode(o.roomCategory)) || o.roomType || 'Room',
    // bed/description/amenities: for BOTH sources — the connector provides Amadeus's real
    // per-offer bedType/description (WI #1079) and amenityDetails (WI #1082) too, so these
    // must no longer be gated behind isExtranet. maxOcc stays Extranet-only below — Amadeus
    // carries no per-offer occupancy cap in this API surface. null (not a fabricated "2", WI
    // #1098) when genuinely absent — offer-detail.component.ts hides the badge entirely rather
    // than claim a capacity nobody ever confirmed.
    bed: o.roomBedConfig || '', bedCount: isExtranet ? undefined : o.roomBedCount,
    maxOcc: isExtranet ? (o.roomMaxOccupancy ?? null) : null,
    // size/photos/highlights remain genuinely Extranet-only — Amadeus carries no per-offer
    // equivalent of these in this API surface, so the room-media block simply stays partly
    // empty for Amadeus-sourced rooms (see offer-detail.component.ts's *ngIf on this data —
    // it's data-driven, not source-gated, so it still shows once description/bed/services
    // alone are present).
    size: isExtranet ? o.roomSizeSqm : undefined, description: o.roomDescription,
    photos: isExtranet ? (o.roomPhotos || []) : [],
    // View has no dedicated chip slot — folded into highlights as one more badge, same as
    // the standalone-Extranet path (mapExtranetHotel below).
    highlights: isExtranet ? [...(o.roomHighlights || []), ...(o.roomView ? [o.roomView] : [])] : [],
    // Amadeus's raw amenityIdentifier values render through the same amenityLabel()
    // title-case fallback already used for hotel-level Amadeus amenities — no separate
    // translation table needed for the offer-level ones.
    services: o.roomAmenities || [], plans: [],
  };
}

/** Per-night share of a stay total — NOT rounded (WI 3976). It is a display figure only, shown
 *  to the currency's minor unit by money(); every total is built from the stay total itself.
 *  Rounding it to whole units (the old Math.round) drifted a 30-night stay up to ~15 per room
 *  from the amount charged once the rounded nightly was multiplied back. */
function perNight(total: number, n: number): number {
  return n > 0 ? total / n : total;
}

/** Per-room stay total in the reservation currency — the offer's `reservationTotal`, verbatim.
 *  Three-state passthrough (feature: hotel-reservation-currency):
 *   undefined → the response predates the contract (no reservation fields on the wire);
 *   null      → BE says this offer is genuinely un-priceable in the requested currency (§4);
 *   number    → the converted per-room stay total. */
function resStay(reservationTotal: number | null | undefined): number | null | undefined {
  if (reservationTotal === undefined) return undefined;
  if (reservationTotal === null) return null;
  return Number(reservationTotal);
}

/** Per-night amount in the reservation currency, derived from the offer's stay-level
 *  `reservationTotal` exactly like the native `nightly` is derived from `totalPrice` (both via
 *  perNight(), so converted and native figures stay display-consistent). Same three-state
 *  passthrough as resStay(). */
function deriveResNightly(reservationTotal: number | null | undefined, n: number): number | null | undefined {
  const stay = resStay(reservationTotal);
  return stay == null ? stay : perNight(stay, n);
}

/** One offer -> one RatePlan. Split out of groupOffersIntoRooms() for the same reason as
 *  buildRoomShell() above. */
function buildRatePlan(o: AmadeusOfferDto, n: number): RatePlan {
  const isExtranet = o.source === 'EXTRANET';
  const total = Number(o.totalPrice ?? 0);
  return {
    ratePlanId: isExtranet ? (o.extranetRatePlanId || o.offerId) : o.offerId, cacheId: o.cacheId,
    // Source-blind booking identity, BOTH sources (see AmadeusOfferDto.bookingOfferId) — when
    // every selected line has one, booking goes out as one offerSelections body and `source`
    // below stays display-only (badge/scope), never a dispatch key.
    bookingOfferId: o.bookingOfferId,
    source: (isExtranet ? 'EXTRANET' : 'AMADEUS') as Source,
    extranetPropertyId: o.extranetPropertyId,
    // Amadeus never sets this (isExtranet guards it, same convention as extranetPropertyId above).
    extranetScope: isExtranet ? (o.extranetScope as 'MARKETPLACE' | 'PRIVATE' | undefined) : undefined,
    // Both sources now carry per-offer board and cancellation-policy data (Amadeus per
    // HotelConnectorResponseMapper's toHotelOffer(HotelOfferResponseDto,...) in
    // be-flight-search-service, be-flight-search-service PR !1543) — default to Room Only /
    // refundableFromPolicyText()'s own text-parsing fallback only when a value is genuinely
    // absent, rather than special-casing by source.
    board: MEAL_TO_BOARD[o.mealPlan || ''] || 'RO' as Board,
    // Amadeus: prefer the connector-resolved value (HotelOfferDto.refundable(), already correct
    // per be-amadeus-hotel-conn-service's HotelOffersMapper.deriveRefundable — WI #1095 audit
    // found this was on the wire but never consumed here) over re-deriving from raw policyDetails
    // text — mirrors HotelPostMergeFilter.deriveRefundable()'s same "prefer resolved value,
    // fallback for the other source" pattern in be-flight-search-service. Extranet's own
    // connector doesn't populate `refundable` (always undefined for it), so it keeps using the
    // text-parsing fallback, unchanged.
    refundable: o.refundable ?? refundableFromPolicyText(o.policyDetails),
    // Resolved connector-side, both sources now (WI #1098) — no per-source fallback needed,
    // unlike refundable above: both connectors already resolve this themselves before it
    // reaches HotelOfferDto.
    deadlineDate: o.cancellationDeadlineDate ?? null,
    // roomsAvailable is Extranet-only real data (WI #1098) — null for Amadeus, which
    // offer-detail.component.ts must treat as "unknown", never as "sold out".
    nightly: perNight(total, n), stayTotal: total, roomsLeft: o.roomsAvailable ?? null,
    // Reservation-currency pair (feature: hotel-reservation-currency) — the ONLY figures the
    // agent-facing flow renders (decision A); the native nightly above stays data-model-only.
    resNightly: deriveResNightly(o.reservationTotal, n),
    resStayTotal: resStay(o.reservationTotal),
    resCurrency: o.reservationCurrency ?? undefined,
  };
}

/** roomType -> friendly name, when the hotel's content actually correlates the two (its own
 *  "providerReferenceId" matching the offer's "roomType") — in practice this is sparse in
 *  Amadeus's sandbox (providerReferenceId is usually just "ALL"), so this is best-effort: falls
 *  straight back to the raw roomType code (e.g. "*1K") when no match exists, same as before.
 *  `|| []`, not just a default param: a default only covers `undefined`, and a standalone
 *  Extranet hotel's synthetic content sent this as a literal JSON `null` — which crashed this
 *  whole function on `.filter()`, which threw out of the *entire* page's `.map()`, which RxJS's
 *  catchError silently swallowed into an empty array — one bad hotel blanked the whole results
 *  page, Amadeus hotels included, with no console error to point at why. */
function buildNameByRoomTypeMap(
  roomDetails: { providerReferenceId?: string; name?: string }[] | null,
): Map<string, string> {
  return new Map(
    (roomDetails || []).filter(rd => rd.providerReferenceId && rd.providerReferenceId !== 'ALL' && rd.name)
      .map(rd => [rd.providerReferenceId as string, rd.name as string]));
}

/** Groups a flat (possibly Phase-3-merged) offer list into the FE's room/rate-plan tree.
 *  Extranet-sourced offers — tagged `source === 'EXTRANET'` after the be-flight-search-service
 *  merge — key by their real `extranetRoomTypeId`, since that id is what booking/refresh need
 *  downstream (a genuine DB UUID primary key, guaranteed unique). Amadeus offers have no such id
 *  at all, so they key by a composite of `roomType` + `roomCategory` + `roomBedConfig` (WI #1095 —
 *  a real live capture showed Amadeus using the SAME generic `roomType` code, e.g. "ROH"/
 *  Run-of-House, across genuinely different physical rooms — 9 offers all tagged "ROH" split
 *  evenly between STANDARD_ROOM/TWIN, ACCESSIBLE_ROOM/KING, and STANDARD_ROOM/SINGLE; keying on
 *  `roomType` alone collapsed all 9 into a single room card). This composite join is a sanctioned
 *  design choice, not a stopgap: these three fields are opaque per-provider labels the connector
 *  (be-amadeus-hotel-conn-service) deliberately does NOT resolve into a platform enum (unlike
 *  mealPlan/refundable, which have real cross-source business meaning to interpret) — nothing here
 *  requires knowing what any Amadeus value MEANS, only comparing them for equality, so combining
 *  them client-side isn't the kind of provider-interpretation the connector-normalization rule is
 *  about. The connector DOES guarantee they arrive trimmed/blank-to-null (HotelOffersMapper's
 *  normalizeLabel()), so this join never has to defensively re-clean a raw provider quirk itself.
 *  `roomCategory`/`roomBedConfig` are appended only when present, so hotels where Amadeus DOES
 *  give a specific `roomType` per room (no collision) don't get a needlessly longer key — and a
 *  hotel with many rate plans genuinely sharing one room (same roomType+category+bedType, e.g. the
 *  22-rate "ROH" case seen earlier) still buckets together, since none of the 3 components differ.
 *  Used only for the initial search mapping (mapAmadeusHotel — order doesn't matter there, only
 *  Math.min()/some() aggregates are ever read off it) — the offer-detail page's own refresh trusts
 *  be-flight-search-service's pre-grouped, pre-ordered rooms instead (see
 *  roomsFromRefreshResponse() below, and HotelRoomAssembler.java server-side) rather than
 *  rebuilding the tree from a flat list here. */
function groupOffersIntoRooms(
  offers: AmadeusOfferDto[], crit: HotelSearchCriteria,
  roomDetails: { providerReferenceId?: string; name?: string }[] | null = [],
): HotelRoom[] {
  const n = nights(crit.checkIn || offers[0]?.checkInDate || '', crit.checkOut || offers[0]?.checkOutDate || '') || 1;
  const nameByRoomType = buildNameByRoomTypeMap(roomDetails);
  const byRoom = new Map<string, HotelRoom>();
  offers.forEach((o, i) => {
    const isExtranet = o.source === 'EXTRANET';
    const key = isExtranet
      ? (o.extranetRoomTypeId || 'rt' + i)
      : ([o.roomType, o.roomCategory, o.roomBedConfig].filter(Boolean).join('|') || 'rt' + i);
    let room = byRoom.get(key);
    if (!room) {
      room = buildRoomShell(key, o, nameByRoomType);
      byRoom.set(key, room);
    }
    room.plans.push(buildRatePlan(o, n));
  });
  return Array.from(byRoom.values());
}

/** getOffersForHotel()'s path: trusts be-flight-search-service's pre-grouped, pre-ordered `rooms`
 *  (HotelRoomAssembler.java — Extranet rooms first, then Amadeus rooms ascending by their own
 *  cheapest offer, each room's offers ascending by price) — builds the same per-room/per-plan
 *  display metadata as groupOffersIntoRooms() above via the shared buildRoomShell()/
 *  buildRatePlan() helpers, but neither re-derives the grouping key nor re-sorts anything; room
 *  and offer array order is preserved exactly as the backend returned it. */
function roomsFromRefreshResponse(
  rooms: AmadeusRoomDto[], crit: HotelSearchCriteria,
  roomDetails: { providerReferenceId?: string; name?: string }[] | null = [],
): HotelRoom[] {
  const first = rooms[0]?.offers[0];
  const n = nights(crit.checkIn || first?.checkInDate || '', crit.checkOut || first?.checkOutDate || '') || 1;
  const nameByRoomType = buildNameByRoomTypeMap(roomDetails);
  return rooms
    // Defensive — the backend should never emit an empty group, but never trust that blindly
    // client-side (buildRoomShell needs at least one offer to build display metadata from).
    .filter(r => r.offers.length > 0)
    .map(r => {
      const shell = buildRoomShell(r.roomTypeId, r.offers[0], nameByRoomType);
      shell.plans = r.offers.map(o => buildRatePlan(o, n));
      return shell;
    });
}

export function mapAmadeusHotel(r: AmadeusHotelResultDto, c: HotelSearchCriteria): HotelOffer {
  const h = r.hotel;
  const stars = h.starRating || 0;
  const photos = (h.content?.photos || []).map(p => p.imageUrl).filter((u): u is string => !!u);
  // amenityIdentifier is the specific amenity (e.g. "HEALTH_CLUB", "SAUNA"); amenityType is only
  // the broad category ("HotelAmenities", "SafetyFacilities") shared by dozens of records — using
  // it first rendered the same handful of category labels repeated ~40x instead of real amenities.
  const amenities = [...new Set((h.content?.hotelFacilities?.amenityDetails || [])
    .map(a => a.amenityIdentifier || a.amenityType).filter((a): a is string => !!a))];
  // Real guest-rating data when the content API returns one — falls back to the stars-derived
  // proxy otherwise (kept as-is, never a regression to 0). Without this, "Star rating" and
  // "Guest rating" sort options were mathematically identical (rating was always stars*2),
  // making the sort dropdown appear broken when toggling between those two options.
  const contentRating = Number(h.content?.rating);
  const guestRating = Number.isFinite(contentRating) && contentRating > 0
    ? contentRating : (stars ? +(stars * 2).toFixed(1) : 0);
  const base: HotelOffer = {
    id: h.hotelId, name: h.name,
    city: c.dest, country: '', // Amadeus's HotelDto carries a cityCode, not a display city/country name
    stars, source: 'AMADEUS', rating: guestRating,
    address: h.address || '',
    lat: Number(h.latitude) || 0, lng: Number(h.longitude) || 0,
    amenities, description: h.content?.description,
    photos: photos.length ? photos : [NO_PHOTO_PLACEHOLDER],
    rooms: [],
  };
  // No more hotel-wide policy overlay here: AMADEUS-sourced plans now carry their own accurate
  // per-offer `refundable` from groupOffersIntoRooms() too (same per-offer policyDetails
  // Extranet always had) — the old overlay was the FE-side half of a bug where a duplicate-hotel
  // merge's losing twin silently inherited whichever HotelDto's hotel-wide policy the merge kept
  // as primary, regardless of the offer's own real policy.
  base.rooms = groupOffersIntoRooms(r.offers || [], c, h.content?.roomDetails);
  // A standalone Extranet-only property (be-flight-search-service's merge now injects these when
  // there's no Amadeus counterpart at all — every plan tagged EXTRANET, none AMADEUS) still comes
  // back through this same /hotels/search/by-geo response, so the hardcoded 'AMADEUS' badge/source
  // would mislabel it. A genuinely Phase-3-merged hotel (real offers from both connectors) used to
  // just keep 'AMADEUS' as its primary source regardless of how much of its inventory was actually
  // Extranet — the badge always read Amadeus for any hotel with real overlap between the two
  // connectors. 'MIXED' makes that visible instead of hiding it.
  const allPlans = base.rooms.flatMap(room => room.plans);
  const hasExtranet = allPlans.some(p => p.source === 'EXTRANET');
  const hasAmadeus = allPlans.some(p => p.source === 'AMADEUS');
  if (hasExtranet && hasAmadeus) {
    base.source = 'MIXED';
  } else if (hasExtranet) {
    base.source = 'EXTRANET';
  }
  return base;
}

/* ===== be-flight-search-service /hotels/search + reprice response (mirror of the Java DTOs;
 * see com.hilbertech.ota.hotel.dto[.hotelcontent] in be-flight-search-service) ===== */
interface AmadeusSearchResponseDto {
  status: 'COMPLETED' | 'FAILED'; hotels?: AmadeusHotelResultDto[]; totalCount?: number; failureReason?: string;
  // v2 ("server-side filters") only — see com.hilbertech.ota.hotel.dto.HotelSearchResponseDto in
  // be-flight-search-service. All four undefined on any v1 response (@JsonInclude NON_NULL,
  // neither v1 factory method sets them).
  hasMore?: boolean;
  totalCountIsExact?: boolean;
  priceRangeMin?: number;
  priceRangeMax?: number;
  // Effective (post-entitlement-clamp) sources actually queried — see HotelSearchResult#querySources.
  querySources?: string[];
}
interface AmadeusRepriceResponseDto {
  status?: string; priceConfirmed?: boolean; refreshed?: boolean;
  offer?: AmadeusOfferDto; hotelOffers?: AmadeusOfferDto[];
}
interface AmadeusHotelResultDto { hotel: AmadeusHotelDto; offers?: AmadeusOfferDto[]; }
interface AmadeusHotelDto {
  hotelId: string; name: string; chainCode?: string; cityCode?: string; address?: string;
  latitude?: number; longitude?: number; starRating?: number; thumbnailUrl?: string;
  content?: AmadeusHotelContentDto;
}
interface AmadeusHotelContentDto {
  rating?: string; description?: string;
  photos?: { imageUrl?: string }[];
  policyDetails?: { policyType?: string; description?: string }[];
  hotelFacilities?: { amenityDetails?: { amenityType?: string; amenityIdentifier?: string }[] };
  roomDetails?: { providerReferenceId?: string; name?: string }[];
}
interface AmadeusOfferDto {
  offerId: string; cacheId: string; amadeusOfferId?: string; hotelId?: string; hotelName?: string;
  // Source-blind booking identity (feat: hotel-booking-identity-source-blind) — UUID minted by
  // be-flight-search-service for EVERY offer of BOTH sources (persisted in its
  // hotel_offer_identity store, resolvable via GET /hotels/booking-offers/{id}). Distinct from
  // `offerId` above (per-response display/correlation id). Optional only for responses from
  // before the identity store shipped. Reprice responses carry a NEW one per fresh cacheId —
  // see confirmOneLine()'s id swap.
  bookingOfferId?: string;
  checkInDate?: string; checkOutDate?: string; roomQuantity?: string; roomType?: string; rateCode?: string;
  currency?: string; totalPrice?: number; basePrice?: number; taxes?: number; priceConfirmed?: boolean;
  // Reservation-currency pair (feature: hotel-reservation-currency) — present whenever the search
  // was made with a `currency` param (this FE always sends one). The native totalPrice/basePrice/
  // taxes/currency above stay untouched in the SOURCE's own currency; these four carry the same
  // amounts restated in the requested reservation currency. reservationTotal === null (as opposed
  // to absent) means the FX engine genuinely cannot price this offer in that currency right now —
  // the offer is still returned and must be rendered flagged (§4), never hidden.
  reservationTotal?: number | null;
  reservationBasePrice?: number | null;
  reservationTaxes?: number | null;
  reservationCurrency?: string | null;
  // Phase-3 merge fields (com.hilbertech.ota.hotel.enums.OfferSource + HotelOfferDto) — absent on
  // responses from before the merge shipped, hence optional; groupOffersIntoRooms() defaults a
  // missing `source` to AMADEUS so old cached pages keep behaving exactly as before.
  source?: string;
  extranetPropertyId?: string; extranetRoomTypeId?: string; extranetRatePlanId?: string;
  // EXTRANET only (feature: hotel-b2b-source-selection) — 'MARKETPLACE' | 'PRIVATE', already on
  // the wire (com.hilbertech.ota.hotel.dto.HotelOfferDto#extranetScope) but never declared here
  // before. See RatePlan#extranetScope / hotelExtranetScope() in models.ts.
  extranetScope?: string;
  // Extranet-only — the room type's human-readable display name (roomType above is its code,
  // e.g. "STD-DBL"). See groupOffersIntoRooms() below.
  roomTypeName?: string;
  // Amadeus-only — a broad, semi-readable room category (e.g. "STANDARD_ROOM"), used as a name
  // fallback below roomTypeName/nameByRoomType. See groupOffersIntoRooms() below.
  roomCategory?: string;
  // Per-offer cancellation policy, both sources now (Amadeus per HotelConnectorResponseMapper's
  // toHotelOffer(HotelOfferResponseDto,...) in be-flight-search-service — see
  // refundableFromPolicyText()). policyProperties carries Amadeus's structured signal (e.g.
  // { cancellationRefund: "REFUNDABLE_UP_TO_DEADLINE" | "NON_REFUNDABLE" }); Extranet entries
  // don't set it.
  policyDetails?: { policyType?: string; description?: string; policyProperties?: Record<string, unknown> }[];
  // Amadeus-only — resolved server-side by be-amadeus-hotel-conn-service's
  // HotelOffersMapper.deriveRefundable() (WI #1078 retest) and passed straight through by
  // HotelConnectorResponseMapper.toHotelOffer(HotelOfferResponseDto,...). Was captured on the
  // wire since then but never declared here (WI #1095 audit found this FE type never grew a field
  // for it, so it was silently dropped and every Amadeus offer kept re-deriving refundable from
  // raw policyDetails text instead of trusting the already-resolved value). Prefer this over
  // refundableFromPolicyText() wherever the offer is Amadeus-sourced — undefined for Extranet
  // (its own connector doesn't populate it), which keeps using the text-parsing fallback.
  refundable?: boolean;
  // Amadeus-only — number of beds in the room (typeEstimated.beds via
  // HotelOfferDto.roomBedCount, WI #1095 audit). Display-only (e.g. "2 × Twin"); not part of the
  // room-grouping key below.
  roomBedCount?: number;
  // Resolved connector-side (WI #1098), both sources — the calendar date after which cancellation
  // is no longer free. Both be-amadeus-hotel-conn-service (from policies.cancellations[].deadline)
  // and be-hotel-extranet-service (from ExtranetRatePlan.cancellationDeadlineDaysBefore + checkIn)
  // resolve this themselves now — no per-source derivation needed here, unlike refundable above.
  cancellationDeadlineDate?: string | null;
  // Extranet-only — real inventory count for this room type across the stay (the minimum across
  // every night, be-hotel-extranet-service's ExtranetOfferService#minRoomsAvailable). Undefined
  // for Amadeus: no equivalent field exists anywhere in that connector's API surface (confirmed
  // via full grep of dto/amadeus/response/*.java).
  roomsAvailable?: number | null;
  // Per-offer meal plan, both sources now — Amadeus's connector DTO carries this per-offer too
  // (see groupOffersIntoRooms' board mapping below).
  mealPlan?: string;
  // roomBedConfig/roomDescription/roomAmenities: both sources now (Amadeus per
  // HotelConnectorResponseMapper's toHotelOffer(HotelOfferResponseDto,...) — WI #1079/#1082).
  // roomMaxOccupancy/roomSizeSqm/roomPhotos/roomHighlights remain Extranet-only. See
  // groupOffersIntoRooms() below.
  roomBedConfig?: string;
  roomMaxOccupancy?: number;
  roomSizeSqm?: number;
  roomDescription?: string;
  roomPhotos?: string[];
  roomHighlights?: string[];
  roomAmenities?: string[];
  // Extranet-only — guest-facing View modifier (e.g. "SEA_VIEW", "HARAM_VIEW"), rendered as one
  // more chip in the highlights row (see groupOffersIntoRooms() below) rather than its own field,
  // since there's no distinct "view" slot on HotelRoom.
  roomView?: string;
}
/** be-flight-search-service's HotelRoomAssembler — one room's offers, already grouped and
 *  price-sorted server-side. roomTypeId matches HotelRoom.roomTypeId's own naming for this same
 *  concept (a real UUID for Extranet, a roomType|roomCategory|roomBedConfig composite for
 *  Amadeus) so it wires straight through unrenamed. Must NOT be re-grouped or re-sorted
 *  client-side — see roomsFromRefreshResponse() below. */
interface AmadeusRoomDto {
  roomTypeId: string;
  offers: AmadeusOfferDto[];
}
/** be-flight-search-service GET /hotels/{hotelId}/offers/refresh response. `offers` is the
 *  deprecated flat shape, kept only for the BE/FE deploy transition window (optional here since
 *  a fresh BE always sends it, but a stale cached response type must not assume it) — the FE
 *  never reads it once `rooms` is live (see getOffersForHotel()). */
interface AmadeusOffersRefreshResponseDto {
  hotelId: string;
  offers?: AmadeusOfferDto[];
  rooms: AmadeusRoomDto[];
  // Effective (post-entitlement-clamp) sources this refresh actually queried — see
  // HotelOffer#querySources.
  querySources?: SearchSource[];
}

/** Map an Extranet hotel + its flat offer list into our grouped HotelOffer domain model.
 *  Offers are grouped by roomTypeId; price.total (stay) → per-night; mealPlan → board.
 *  Note: hotel-level photos come from the property's real uploaded gallery (h.photos) when
 *  present; NO_PHOTO_PLACEHOLDER is used as a fallback for properties with none uploaded yet.
 *  guest-rating still has no real BE source and stays placeholder; deadlineDate/roomsAvailable
 *  are real connector-resolved data now (WI #1098) — see cancellationDeadlineDate/roomsAvailable
 *  on ExtranetOfferDto above. */
export function mapExtranetHotel(h: ExtranetHotelDto, c: HotelSearchCriteria): HotelOffer {
  const n = nights(c.checkIn, c.checkOut);
  const byRoom = new Map<string, HotelRoom>();
  (h.offers || []).forEach((o, i) => {
    const key = o.roomTypeId || o.roomDetails?.code || 'rt' + i;
    let room = byRoom.get(key);
    if (!room) {
      const rd = o.roomDetails;
      room = {
        roomTypeId: key, room: rd?.name || 'Room',
        bed: rd?.bedConfig || '', maxOcc: rd?.maxOccupancy ?? null,
        size: rd?.sizeSqm, description: rd?.description, photos: rd?.photos || [],
        // View has no dedicated chip slot — folded into highlights as one more badge.
        highlights: [...(rd?.highlights || []), ...(rd?.view ? [rd.view] : [])],
        services: rd?.amenities || [], plans: [],
      };
      byRoom.set(key, room);
    }
    const total = Number(o.price?.total ?? 0);
    room.plans.push({
      ratePlanId: o.ratePlanId || o.id, board: MEAL_TO_BOARD[o.mealPlan || ''] || 'RO',
      refundable: refundableFromPolicyText(o.policyDetails),
      deadlineDate: o.cancellationDeadlineDate ?? null,
      nightly: perNight(total, n), stayTotal: total, roomsLeft: o.roomsAvailable ?? null,
      resNightly: deriveResNightly(o.reservationTotal, n),
      resStayTotal: resStay(o.reservationTotal),
      resCurrency: o.reservationCurrency ?? undefined,
      source: 'EXTRANET', extranetPropertyId: h.id,
    });
  });
  const stars = h.starRating || 0;
  return {
    id: h.id, name: h.name,
    city: h.hotelAddress?.cityName || c.dest, country: h.hotelAddress?.countryCode || '',
    stars, source: 'EXTRANET', rating: stars ? +(stars * 2).toFixed(1) : 0,
    address: h.hotelAddress?.lines || '',
    lat: h.geoLocation?.latitude || 0, lng: h.geoLocation?.longitude || 0,
    amenities: h.amenities || [],
    description: h.description,
    distanceKm: h.distanceKm ?? undefined,
    photos: h.photos?.length ? h.photos : [NO_PHOTO_PLACEHOLDER],
    rooms: Array.from(byRoom.values()),
  };
}
